/**
 * 「区間を持つ要素」(オーバーレイ・映像エフェクト) をトリム / 速度 / 分割に追従させる共通ロジック。
 * docs/features/video-editor-capability-gaps-plan.md §4.1-F / §4.1-S。
 *
 * 同じ意味・同じ数式の実装が 3 か所にある (どれかを変えたら必ず 3 つとも変える):
 * - ここ (image-generator FE: プレビュー・UI・書き出し)
 * - video-editor リポジトリの src/schema/timedSpan.ts (Cloud Run の MP4 レンダラー)
 * - backend/services/video_edit/timed_span.py (AI 提案の正規化・部分操作)
 *
 * ## なぜ切り出したか
 *
 * オーバーレイ (§4.1-F) と映像エフェクト (§4.1-S) は「シーンの表示秒を基準にした区間を持ち、
 * 区間を省略するとシーン全体」という**まったく同じ時間モデル**を持つ。テロップも同じ 3 分岐
 * (前半だけ / 後半だけ / 境界をまたぐ) で分割する。**時間の式を種類ごとに書き直すと必ずずれる**
 * (§7 の 29 回目: 表示尺の判定が 1 か所だけ生の値を見ていて 3 実装が割れた) ので、
 * 丸め・境界の扱いを含めてここ 1 本に集約する。
 *
 * ## 規約
 *
 * - `startSec` を省略 = 0 から / `endSec` を省略 = 区間の終わりまで。**両方省略 = 全区間**で、
 *   この「全区間」の要素はトリム・速度では**触らない** (意味が「シーン全体」のままだから)
 * - 秒は小数第 2 位まで (half-up)。`Math.round` は負値で half-down になるので使わない
 * - 分割では「全区間」の要素を両側に複製する (後半は新しい id)
 */

/**
 * 区間の切り出し / 分割で生まれた要素に、**その種類だけが持つ時間依存の属性**を追従させるための
 * フック (§4.1-V のオーバーレイのキーフレーム)。区間の計算そのものはこのモジュールが持ち、
 * 呼び出し側は「produced (詰め直した後) と original (詰め直す前) を見て自分の属性を直す」だけ。
 *
 * `part` は分割のどちら側か (`clampSpansToDuration` は常に `'first'`)。
 * **produced を書き換えず、新しいオブジェクトを返すこと**。
 */
export type SpanPart = 'first' | 'second';
export type SpanAdjust<T> = (produced: T, original: T, part: SpanPart) => T;

/** 区間を持つ要素の最小の形 (Overlay / Effect が満たす)。 */
export interface TimedSpan {
  id: string;
  /** 表示区間の開始 (省略 = 0)。 */
  startSec?: number;
  /** 表示区間の終わり (省略 = 全区間の終わり)。 */
  endSec?: number;
}

/**
 * half-up の丸め (Python の `_round_half_up` と同値)。
 * JS の `Math.round` は負値で half-down になる (-0.5 → -0) ので自前で持つ。
 */
export function roundHalfUp(value: number, digits: number): number {
  const factor = 10 ** digits;
  const scaled = value * factor;
  const rounded = scaled >= 0 ? Math.floor(scaled + 0.5) : -Math.floor(-scaled + 0.5);
  // -0 を 0 に揃える (Python の丸めは 0.0 を返す)
  return rounded === 0 ? 0 : rounded / factor;
}

/** 秒の丸め (保存形は小数第 2 位まで)。 */
function roundSec(value: number): number {
  return roundHalfUp(value, 2);
}

/** その要素が「全区間」(= シーン全体) を意味するか。 */
export function isWholeSpan(span: Pick<TimedSpan, 'startSec' | 'endSec'>): boolean {
  return span.startSec === undefined && span.endSec === undefined;
}

/** その時刻に効いているか (start <= sec < end。end 省略時は durationSec)。 */
export function isSpanActiveAt(
  span: Pick<TimedSpan, 'startSec' | 'endSec'>,
  sec: number,
  durationSec: number,
): boolean {
  const start = span.startSec ?? 0;
  const end = span.endSec ?? durationSec;
  return sec >= start && sec < end;
}

/**
 * 新しい表示尺に合わせて区間を詰める (テロップと同じ規則)。
 * 区間が成立しなくなったもの (start >= end) は `dropped` に落とす。
 * 「全区間」の要素はそのまま残す。
 */
export function clampSpansToDuration<T extends TimedSpan>(
  spans: readonly T[] | undefined,
  durationSec: number,
  adjust?: SpanAdjust<T>,
): { kept: T[]; dropped: T[] } {
  const kept: T[] = [];
  const dropped: T[] = [];
  const apply = (produced: T, original: T): T => (adjust ? adjust(produced, original, 'first') : produced);
  for (const span of spans ?? []) {
    if (isWholeSpan(span)) {
      kept.push(apply(span, span));
      continue;
    }
    const start = Math.min(Math.max(span.startSec ?? 0, 0), durationSec);
    const end = Math.min(Math.max(span.endSec ?? durationSec, 0), durationSec);
    if (start >= end) {
      dropped.push(span);
      continue;
    }
    const next: T = { ...span };
    if (start > 0) next.startSec = roundSec(start);
    else delete next.startSec;
    next.endSec = roundSec(end);
    kept.push(apply(next, span));
  }
  return { kept, dropped };
}

/**
 * 速度変更でシーンの表示尺が変わったときの詰め直し (テロップの rescaleCaptionsForSpeed と同じ比率)。
 * 「全区間」の要素は触らない (意味が「シーン全体」なので伸縮させる対象ではない)。
 */
export function rescaleSpansForSpeed<T extends TimedSpan>(
  spans: T[] | undefined,
  prevSpeed: number,
  nextSpeed: number,
): T[] | undefined {
  if (!spans || spans.length === 0) return spans;
  if (!(prevSpeed > 0) || !(nextSpeed > 0) || Math.abs(prevSpeed - nextSpeed) < 1e-9) return spans;
  const ratio = prevSpeed / nextSpeed;
  return spans.map((span) => {
    if (isWholeSpan(span)) return span;
    const next: T = { ...span };
    if (span.startSec !== undefined) next.startSec = roundSec(span.startSec * ratio);
    if (span.endSec !== undefined) next.endSec = roundSec(span.endSec * ratio);
    return next;
  });
}

/**
 * シーンを表示秒 `atSec` で分割したときの (前半, 後半)。
 * テロップ (`_op_split_item`) と同じ 3 分岐: 前半だけ / 後半だけ / 境界をまたぐものは切り分ける。
 * 「全区間」の要素は両方に複製する (後半は新しい id)。
 */
export function splitSpans<T extends TimedSpan>(
  spans: readonly T[] | undefined,
  atSec: number,
  makeId: (index: number) => string,
  adjust?: SpanAdjust<T>,
): { first: T[]; second: T[] } {
  const first: T[] = [];
  const second: T[] = [];
  let created = 0;
  const apply = (produced: T, original: T, part: SpanPart): T =>
    (adjust ? adjust(produced, original, part) : produced);
  for (const span of spans ?? []) {
    if (isWholeSpan(span)) {
      first.push(apply(span, span, 'first'));
      second.push(apply({ ...span, id: makeId(created++) }, span, 'second'));
      continue;
    }
    const start = span.startSec ?? 0;
    const end = span.endSec;
    if (end !== undefined && end <= atSec + 1e-9) {
      first.push(apply(span, span, 'first'));
      continue;
    }
    if (start >= atSec - 1e-9) {
      const moved: T = { ...span };
      const movedStart = roundSec(Math.max(0, start - atSec));
      if (movedStart > 0) moved.startSec = movedStart;
      else delete moved.startSec;
      if (end !== undefined) moved.endSec = roundSec(Math.max(movedStart, end - atSec));
      moved.id = makeId(created++);
      second.push(apply(moved, span, 'second'));
      continue;
    }
    // 境界をまたぐ: 前半は atSec で切り、後半は 0 から続ける
    const head: T = { ...span, endSec: roundSec(atSec) };
    first.push(apply(head, span, 'first'));
    const tail: T = { ...span, id: makeId(created++) };
    delete tail.startSec;
    if (end !== undefined) tail.endSec = roundSec(end - atSec);
    second.push(apply(tail, span, 'second'));
  }
  return { first, second };
}
